import { SoundFont2 as EsmSoundFont2 } from "soundfont2-esm";

const DRUM_BANK = 128;
const DRUM_BANK_MIN = 120; // XG/GS drum area — fallback, если нет bank=128 program=0
const DRUM_PROGRAM = 0;
const MELODIC_BANK = 0;

export type ProgramMap = {
  /** Имя инструмента для мелодической ноты (GM program 0..127, bank 0). */
  getMelodic(program: number): string | null;
  /** Имя ударной установки (GM bank 128 program 0) или null. */
  getDrumKit(): string | null;
  /** Полный список имён инструментов в SF2 (для диагностики). */
  readonly instrumentNames: ReadonlyArray<string>;
};

type EsmPreset = {
  header: { name: string; preset: number; bank: number };
  zones: Array<{
    instrument?: { header: { name: string } } | null;
  }>;
};

/**
 * Резолвит имя инструмента, на который ссылается пресет.
 * Берём первый зон, у которого есть instrument.
 */
const instrumentNameFromPreset = (preset: EsmPreset): string | null => {
  for (const zone of preset.zones) {
    if (zone.instrument) return zone.instrument.header.name;
  }
  return null;
};

/**
 * Строит карту GM-program → instrument-name для мелодических
 * пресетов (bank 0) и для ударной установки (bank 128 program 0).
 *
 * Если в SF2 для одного program указано несколько пресетов (например,
 * дубликаты в GeneralUser GS) — берётся первый встреченный.
 *
 * Drum fallback: если пресет bank=128 program=0 отсутствует (например,
 * в «урезанных» SF2 или в GS-вариантах, где барабаны лежат в bank>=120),
 * ищется **любой** пресет из банков `[120..128]` с program=0 — это
 * покрывает XG (`bank=120`), GS (`bank=121..127`) и GM2 drum kits.
 */
export const buildProgramMap = (data: Uint8Array): ProgramMap => {
  const parsed = new EsmSoundFont2(data);
  const melodic = new Map<number, string>();
  let drumKit: string | null = null;
  let drumKitFallback: string | null = null;

  for (const presetRaw of parsed.presets as unknown as EsmPreset[]) {
    const { bank, preset: program } = presetRaw.header;
    const instName = instrumentNameFromPreset(presetRaw);
    if (!instName) continue;
    if (bank === MELODIC_BANK && program >= 0 && program <= 127) {
      if (!melodic.has(program)) melodic.set(program, instName);
    } else if (bank === DRUM_BANK && program === DRUM_PROGRAM) {
      if (!drumKit) drumKit = instName;
    } else if (
      bank >= DRUM_BANK_MIN &&
      bank <= DRUM_BANK &&
      program === DRUM_PROGRAM
    ) {
      if (!drumKitFallback) drumKitFallback = instName;
    }
  }

  const instrumentNames = (parsed.instruments as Array<{ header: { name: string } }>)
    .map((i) => i.header.name);

  return {
    getMelodic: (program) => melodic.get(program) ?? null,
    getDrumKit: () => drumKit ?? drumKitFallback,
    get instrumentNames() {
      return instrumentNames;
    },
  };
};